type CoderNestedOutput = {
  result?: unknown;
  stdout?: unknown[];
  stderr?: unknown[];
  codeRuntime?: number;
  success?: boolean;
};

type CoderStructuredOutput = {
  script?: string;
  code?: string;
  result?: unknown;
  summary?: unknown;
  output?: CoderNestedOutput | string;
};

const EMPTY_CODER_RESULT_MARKERS = new Set([
  '(no output produced)',
  '(No output produced)',
]);

function readNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
}

function preferLongerString(
  primary: string | undefined,
  fallback: string | undefined,
): string | undefined {
  if (!primary) {
    return fallback;
  }

  if (!fallback) {
    return primary;
  }

  return primary.length >= fallback.length ? primary : fallback;
}

function readStdoutLines(stdout: unknown): string {
  if (!Array.isArray(stdout)) {
    return '';
  }

  return stdout
    .map((entry) => {
      if (typeof entry === 'string') {
        return entry;
      }

      if (entry === undefined || entry === null) {
        return '';
      }

      return String(entry);
    })
    .filter((entry) => entry.trim().length > 0)
    .join('\n');
}

function pythonFailureText(value: string): string | undefined {
  const trimmed = value.trim();

  if (!trimmed.startsWith('{')) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }

    const record = parsed as Record<string, unknown>;

    if (record.success !== false) {
      return undefined;
    }

    const stderr = readStdoutLines(record.stderr);

    return stderr || undefined;
  } catch {
    return undefined;
  }
}

function displayCoderText(value: string): string {
  return pythonFailureText(value) ?? value;
}

function isEmptyCoderResultMarker(value: string | undefined): boolean {
  if (!value) {
    return true;
  }

  return EMPTY_CODER_RESULT_MARKERS.has(value.trim());
}

function readCoderSummaryText(
  record: CoderStructuredOutput,
): string | undefined {
  if (typeof record.summary !== 'string') {
    return undefined;
  }

  const fromSummary = extractCoderMarkdownResult(record.summary);

  if (fromSummary) {
    return fromSummary;
  }

  const trimmed = record.summary.trim();

  return trimmed.length > 0 ? trimmed : undefined;
}

export function extractCoderMarkdownResult(text: string): string {
  const trimmed = text.trim();

  if (!trimmed) {
    return '';
  }

  const stdoutMatch = trimmed.match(
    /Output \(stdout\):\n([\s\S]*?)(?:\n\n(?:Redirect|Script):|\nScript:\n|$)/,
  );

  if (stdoutMatch?.[1]?.trim()) {
    return stdoutMatch[1].trim();
  }

  const previewMatch = trimmed.match(
    /Output preview:\s*([\s\S]*?)(?:\n\nFull results saved to:|\nFull results saved to:|$)/i,
  );

  if (previewMatch?.[1]?.trim()) {
    return previewMatch[1]
      .trim()
      .replace(/\.\.\.$/, '')
      .trim();
  }

  return '';
}

export function readCoderResultFromStructuredOutput(
  output: unknown,
): string | undefined {
  if (typeof output === 'string') {
    const fromMarkdown = extractCoderMarkdownResult(output);

    if (fromMarkdown) {
      return fromMarkdown;
    }

    try {
      return readCoderResultFromStructuredOutput(JSON.parse(output));
    } catch {
      return undefined;
    }
  }

  if (!output || typeof output !== 'object') {
    return undefined;
  }

  const record = output as CoderStructuredOutput;
  const nested =
    typeof record.output === 'object' && record.output !== null
      ? record.output
      : undefined;

  const fromNestedResult = readNonEmptyString(
    typeof nested?.result === 'string'
      ? nested.result
      : nested?.result !== undefined
        ? String(nested.result)
        : undefined,
  );

  if (fromNestedResult && !isEmptyCoderResultMarker(fromNestedResult)) {
    return displayCoderText(fromNestedResult);
  }

  const fromStderr = readStdoutLines(nested?.stderr);

  if (nested?.success === false && fromStderr) {
    return fromStderr;
  }

  const fromStdout = readStdoutLines(nested?.stdout);

  if (fromStdout) {
    return fromStdout;
  }

  if (typeof record.output === 'string') {
    const nestedStringResult = readNonEmptyString(record.output);

    if (nestedStringResult && !isEmptyCoderResultMarker(nestedStringResult)) {
      return displayCoderText(nestedStringResult);
    }
  }

  const fromTopLevelResult = readNonEmptyString(
    typeof record.result === 'string'
      ? record.result
      : record.result !== undefined
        ? String(record.result)
        : undefined,
  );

  if (fromTopLevelResult && !isEmptyCoderResultMarker(fromTopLevelResult)) {
    return displayCoderText(fromTopLevelResult);
  }

  const fromSummary = readCoderSummaryText(record);

  if (fromSummary) {
    return fromSummary;
  }

  return undefined;
}

export function resolveCoderDisplayResult({
  output,
  message,
}: {
  output: unknown;
  message?: string;
}): string | undefined {
  const fromOutput = readCoderResultFromStructuredOutput(output);

  if (fromOutput) {
    return fromOutput;
  }

  if (typeof message === 'string') {
    const fromMessage = extractCoderMarkdownResult(message);

    if (fromMessage) {
      return fromMessage;
    }
  }

  return undefined;
}

export function mergeCoderToolOutputValue({
  anchorOutput,
  updateOutput,
}: {
  anchorOutput: unknown;
  updateOutput: unknown;
}): unknown {
  if (typeof anchorOutput !== 'object' || anchorOutput === null) {
    return updateOutput;
  }

  if (typeof updateOutput !== 'object' || updateOutput === null) {
    if (typeof updateOutput === 'string') {
      const anchor = anchorOutput as CoderStructuredOutput;
      const markdownResult = extractCoderMarkdownResult(updateOutput);

      return {
        ...anchor,
        summary: preferLongerString(
          readNonEmptyString(
            typeof anchor.summary === 'string' ? anchor.summary : undefined,
          ),
          updateOutput,
        ),
        result:
          preferLongerString(
            readCoderResultFromStructuredOutput(anchorOutput),
            markdownResult,
          ) ?? markdownResult,
      };
    }

    return updateOutput;
  }

  const anchor = anchorOutput as CoderStructuredOutput;
  const update = updateOutput as CoderStructuredOutput;

  const anchorNested =
    typeof anchor.output === 'object' && anchor.output !== null
      ? anchor.output
      : undefined;
  const updateNested =
    typeof update.output === 'object' && update.output !== null
      ? update.output
      : undefined;

  const mergedNested: CoderNestedOutput | undefined =
    anchorNested || updateNested
      ? (() => {
          const updateStdoutText = readStdoutLines(updateNested?.stdout);
          const anchorStdoutText = readStdoutLines(anchorNested?.stdout);
          const mergedStdoutText = preferLongerString(
            updateStdoutText || undefined,
            anchorStdoutText || undefined,
          );

          return {
            ...anchorNested,
            ...updateNested,
            result:
              preferLongerString(
                readNonEmptyString(
                  typeof updateNested?.result === 'string'
                    ? updateNested.result
                    : undefined,
                ),
                readNonEmptyString(
                  typeof anchorNested?.result === 'string'
                    ? anchorNested.result
                    : undefined,
                ),
              ) ??
              updateNested?.result ??
              anchorNested?.result,
            stdout:
              mergedStdoutText !== undefined
                ? [mergedStdoutText]
                : (updateNested?.stdout ?? anchorNested?.stdout),
            codeRuntime: updateNested?.codeRuntime ?? anchorNested?.codeRuntime,
            success: updateNested?.success ?? anchorNested?.success,
          };
        })()
      : undefined;

  return {
    ...anchor,
    ...update,
    script:
      readNonEmptyString(update.script) ?? readNonEmptyString(anchor.script),
    code: readNonEmptyString(update.code) ?? readNonEmptyString(anchor.code),
    result:
      preferLongerString(
        readNonEmptyString(
          typeof update.result === 'string' ? update.result : undefined,
        ),
        readNonEmptyString(
          typeof anchor.result === 'string' ? anchor.result : undefined,
        ),
      ) ??
      update.result ??
      anchor.result,
    summary:
      preferLongerString(
        readNonEmptyString(
          typeof update.summary === 'string' ? update.summary : undefined,
        ),
        readNonEmptyString(
          typeof anchor.summary === 'string' ? anchor.summary : undefined,
        ),
      ) ??
      update.summary ??
      anchor.summary,
    ...(mergedNested ? { output: mergedNested } : {}),
  };
}

export function isCoderToolNameForMerge(toolName: string | undefined): boolean {
  if (!toolName) {
    return false;
  }

  const normalized = toolName.toLowerCase().replace(/_/g, '-');

  return (
    normalized === 'coder' ||
    normalized === 'sub-agent-coder' ||
    normalized === 'coder-tool' ||
    normalized === 'python' ||
    normalized === 'python-tool'
  );
}

function parseMaybeJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export function mergeCoderToolOutputStrings(
  anchorOutput: string | undefined,
  updateOutput: string | undefined,
): string | undefined {
  if (!anchorOutput?.trim()) {
    return updateOutput;
  }

  if (!updateOutput?.trim()) {
    return anchorOutput;
  }

  const anchorParsed = parseMaybeJson(anchorOutput);
  const updateParsed = parseMaybeJson(updateOutput);

  if (
    typeof anchorParsed === 'object' &&
    anchorParsed !== null &&
    typeof updateParsed === 'object' &&
    updateParsed !== null
  ) {
    return JSON.stringify(
      mergeCoderToolOutputValue({
        anchorOutput: anchorParsed,
        updateOutput: updateParsed,
      }),
    );
  }

  if (typeof anchorParsed === 'object' && anchorParsed !== null) {
    return JSON.stringify(
      mergeCoderToolOutputValue({
        anchorOutput: anchorParsed,
        updateOutput: updateParsed,
      }),
    );
  }

  if (typeof updateParsed === 'object' && updateParsed !== null) {
    return JSON.stringify(
      mergeCoderToolOutputValue({
        anchorOutput: anchorParsed,
        updateOutput: updateParsed,
      }),
    );
  }

  return preferLongerString(updateOutput, anchorOutput);
}
